<template>
  <HrPageLayout
  >
    <div class="employee-profile-content">
      <div v-if="loading" class="hr-loading-wrap">
        <div class="hr-spinner"></div>
        <p class="hr-text-muted">Loading employee details...</p>
      </div>

      <template v-else-if="employee">
        <!-- Summary card -->
        <div class="hr-chart-card hr-mb-4">
          <div class="hr-chart-body">
            <div class="profile-summary-grid">
              <div class="profile-avatar-wrap">
                <div class="profile-avatar">
                  {{ getInitials(employee.firstName, employee.lastName) }}
                </div>
              </div>
              <div class="profile-summary-main">
                <div class="hr-flex hr-flex-wrap hr-align-center hr-gap-2 hr-mb-2 hr-justify-between">
                  <h4 class="hr-mb-0">{{ getFullName() }}</h4>
                  <button
                    type="button"
                    class="hr-btn hr-btn-sm hr-btn-outline-secondary sensitive-toggle"
                    @click="sensitiveRevealed = !sensitiveRevealed"
                  >
                    <i class="bx" :class="sensitiveRevealed ? 'bx-hide' : 'bx-show'"></i>
                    {{ sensitiveRevealed ? "Hide sensitive fields" : "Reveal sensitive fields" }}
                  </button>
                </div>
                <p class="hr-text-muted hr-mb-1">{{ employee.empNo || 'N/A' }}</p>
                <div class="hr-flex hr-gap-2 hr-flex-wrap hr-mb-2">
                  <span :class="['hr-badge', getStatusClass(employee.status)]">{{ employee.status || 'N/A' }}</span>
                  <span :class="['hr-badge', getEmploymentTypeClass(employee.employmentType)]">
                    {{ displayEmploymentType(employee.employmentType) }}
                  </span>
                </div>
                <div class="profile-meta hr-flex hr-flex-wrap hr-gap-3 hr-text-muted">
                  <span><i class="bx bx-building-house"></i> {{ employee.departmentName || 'N/A' }}</span>
                  <span><i class="bx bx-envelope"></i> {{ displayEmail(employee.email) }}</span>
                  <span><i class="bx bx-phone"></i> {{ displayPhone(employee.phone) }}</span>
                  <span><i class="bx bx-id-card"></i> {{ displayNationalId(employee.nationalId) }}</span>
                  <span><i class="bx bx-cake"></i> {{ displayDateOfBirth(employee.dateOfBirth) }}</span>
                  <span v-if="employee.payGroup"><i class="bx bx-group"></i> Pay group: {{ displayPayGroup(employee.payGroup) }}</span>
                  <span><i class="bx bx-calendar"></i> Joined: {{ formatDate(employee.hireDate) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tabs -->
        <div class="hr-chart-card">
          <div class="hr-tabs-bar">
            <button type="button" class="hr-tab" :class="{ 'hr-tab-active': activeTab === 'personal' }" @click="activeTab = 'personal'">
              <i class="bx bx-user"></i> Personal Info
            </button>
            <button type="button" class="hr-tab" :class="{ 'hr-tab-active': activeTab === 'governmentIds' }" @click="activeTab = 'governmentIds'">
              <i class="bx bx-id-card"></i> Government IDs <span class="hr-badge hr-badge-primary hr-badge-sm">{{ governmentIds.length }}</span>
            </button>
            <button type="button" class="hr-tab" :class="{ 'hr-tab-active': activeTab === 'documents' }" @click="activeTab = 'documents'">
              <i class="bx bx-file"></i> Documents <span class="hr-badge hr-badge-primary hr-badge-sm">{{ documents.length }}</span>
            </button>
            <button type="button" class="hr-tab" :class="{ 'hr-tab-active': activeTab === 'contacts' }" @click="activeTab = 'contacts'">
              <i class="bx bx-phone-call"></i> Emergency Contacts <span class="hr-badge hr-badge-warning hr-badge-sm">{{ emergencyContacts.length }}</span>
            </button>
            <button type="button" class="hr-tab" :class="{ 'hr-tab-active': activeTab === 'kin' }" @click="activeTab = 'kin'">
              <i class="bx bx-heart"></i> Next of Kin <span class="hr-badge hr-badge-info hr-badge-sm">{{ nextOfKin.length }}</span>
            </button>
          </div>

          <div class="hr-chart-body tab-content-wrap">
            <!-- Personal -->
            <div v-show="activeTab === 'personal'" class="tab-pane">
              <div class="profile-contact-edit hr-mb-4">
                <div class="profile-contact-edit__head">
                  <h6 class="profile-contact-edit__title"><i class="bx bx-edit-alt"></i> Update contact details</h6>
                  <p class="profile-contact-edit__hint hr-text-muted hr-fs-sm hr-mb-0">
                    You may edit your <strong>email</strong> and <strong>phone number</strong> only. Other fields are maintained by HR.
                  </p>
                </div>
                <div class="profile-contact-fields">
                  <label class="profile-contact-label" for="prof-email">Email</label>
                  <input
                    id="prof-email"
                    v-model.trim="contactForm.email"
                    type="email"
                    class="profile-contact-input"
                    autocomplete="email"
                    placeholder="name@example.com"
                  >
                  <label class="profile-contact-label" for="prof-phone">Phone</label>
                  <input
                    id="prof-phone"
                    v-model.trim="contactForm.phone"
                    type="tel"
                    class="profile-contact-input"
                    autocomplete="tel"
                    placeholder="e.g. 07XX XXX XXX"
                  >
                </div>
                <div class="profile-contact-actions">
                  <button
                    type="button"
                    class="hr-btn hr-btn-primary"
                    :disabled="savingContact || !contactDirty"
                    @click="saveContactDetails"
                  >
                    {{ savingContact ? "Saving…" : "Save changes" }}
                  </button>
                  <span v-if="contactSaveMsg" class="profile-contact-msg" :class="contactSaveOk ? 'text-success' : 'text-danger'">{{ contactSaveMsg }}</span>
                </div>
              </div>

              <div class="hr-form-grid profile-detail-grid">
                <div class="hr-table-wrapper">
                  <table class="hr-table hr-table-borderless">
                    <tbody>
                      <tr><td class="hr-label-cell">Employee Number</td><td>{{ employee.empNo || 'N/A' }}</td></tr>
                      <tr><td class="hr-label-cell">First Name</td><td>{{ employee.firstName || 'N/A' }}</td></tr>
                      <tr><td class="hr-label-cell">Middle Name</td><td>{{ employee.middleName || 'N/A' }}</td></tr>
                      <tr><td class="hr-label-cell">Last Name</td><td>{{ employee.lastName || 'N/A' }}</td></tr>
                      <tr><td class="hr-label-cell">Email</td><td>
                        <template v-if="sensitiveRevealed"><a :href="'mailto:' + employee.email">{{ employee.email || 'N/A' }}</a></template>
                        <template v-else>{{ displayEmail(employee.email) }}</template>
                      </td></tr>
                      <tr><td class="hr-label-cell">Phone</td><td>
                        <template v-if="sensitiveRevealed"><a :href="'tel:' + employee.phone">{{ employee.phone || 'N/A' }}</a></template>
                        <template v-else>{{ displayPhone(employee.phone) }}</template>
                      </td></tr>
                      <tr><td class="hr-label-cell">National ID</td><td>{{ displayNationalId(employee.nationalId) }}</td></tr>
                    </tbody>
                  </table>
                </div>
                <div class="hr-table-wrapper">
                  <table class="hr-table hr-table-borderless">
                    <tbody>
                      <tr><td class="hr-label-cell">Gender</td><td>{{ employee.gender || 'N/A' }}</td></tr>
                      <tr><td class="hr-label-cell">Date of Birth</td><td>{{ displayDateOfBirth(employee.dateOfBirth) }}</td></tr>
                      <tr><td class="hr-label-cell">Nationality</td><td>{{ employee.nationality || 'N/A' }}</td></tr>
                      <tr><td class="hr-label-cell">Department</td><td>{{ employee.departmentName || 'N/A' }}</td></tr>
                      <tr v-if="employee.payGroup"><td class="hr-label-cell">Pay group</td><td>{{ displayPayGroup(employee.payGroup) }}</td></tr>
                      <tr><td class="hr-label-cell">Employment Type</td><td><span :class="['hr-badge', getEmploymentTypeClass(employee.employmentType)]">{{ displayEmploymentType(employee.employmentType) }}</span></td></tr>
                      <tr><td class="hr-label-cell">Hire Date</td><td>{{ formatDate(employee.hireDate) }}</td></tr>
                      <tr><td class="hr-label-cell">Status</td><td><span :class="['hr-badge', getStatusClass(employee.status)]">{{ employee.status || 'N/A' }}</span></td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Government IDs -->
            <div v-show="activeTab === 'governmentIds'" class="tab-pane">
              <div v-if="governmentIds.length === 0" class="hr-empty-state">
                <i class="bx bx-id-card hr-empty-icon"></i>
                <h5>No government IDs available</h5>
                <p class="hr-text-muted">This employee has no government identification records on file</p>
              </div>
              <div v-else class="hr-cards-grid">
                <div v-for="(govId, index) in governmentIds" :key="index" class="hr-info-card">
                  <div class="hr-info-card-header">
                    <div class="hr-info-card-icon"><i class="bx bx-id-card"></i></div>
                    <span :class="['hr-badge', getIdTypeClass(govId.id_type)]">{{ formatIdType(govId.id_type) }}</span>
                  </div>
                  <h6 class="hr-info-card-title">{{ formatIdType(govId.id_type) }}</h6>
                  <div class="hr-info-card-body">
                    <p><i class="bx bx-hash"></i> <strong>Number:</strong> {{ govId.id_number || 'N/A' }}</p>
                    <p><i class="bx bx-flag"></i> <strong>Country:</strong> {{ govId.country_code || 'N/A' }}</p>
                    <p v-if="govId.issue_date"><i class="bx bx-calendar-check"></i> <strong>Issue Date:</strong> {{ formatDate(govId.issue_date) }}</p>
                    <p v-if="govId.expiry_date">
                      <i class="bx bx-calendar-event"></i> <strong>Expiry:</strong> {{ formatDate(govId.expiry_date) }}
                      <span v-if="isExpired(govId.expiry_date)" class="hr-badge hr-badge-danger hr-ml-1"><i class="bx bx-error-circle"></i> Expired</span>
                      <span v-else-if="isExpiringSoon(govId.expiry_date)" class="hr-badge hr-badge-warning hr-ml-1"><i class="bx bx-time-five"></i> Expiring Soon</span>
                    </p>
                    <p v-if="govId.notes" class="hr-fs-sm"><i class="bx bx-note"></i> {{ govId.notes }}</p>
                  </div>
                  <div class="hr-info-card-footer">
                    <span v-if="govId.is_verified === '1'" class="hr-badge hr-badge-success"><i class="bx bx-check-circle"></i> Verified</span>
                    <span v-else class="hr-badge hr-badge-warning"><i class="bx bx-time"></i> Unverified</span>
                    <span v-if="govId.is_active === '1'" class="hr-badge hr-badge-info"><i class="bx bx-check"></i> Active</span>
                    <span v-else class="hr-badge hr-badge-secondary"><i class="bx bx-x"></i> Inactive</span>
                    <p v-if="govId.verified_by" class="hr-fs-sm hr-text-muted hr-mt-1">Verified by: {{ govId.verified_by }} on {{ formatDate(govId.verification_date) }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Documents -->
            <div v-show="activeTab === 'documents'" class="tab-pane">
              <div v-if="documents.length === 0" class="hr-empty-state">
                <i class="bx bx-file hr-empty-icon"></i>
                <h5>No documents available</h5>
                <p class="hr-text-muted">This employee has no documents on file</p>
              </div>
              <div v-else class="hr-cards-grid">
                <div v-for="(doc, index) in documents" :key="index" class="hr-info-card">
                  <div class="hr-info-card-header">
                    <div class="hr-info-card-icon"><i class="bx bx-file"></i></div>
                    <span class="hr-badge hr-badge-secondary">{{ doc.documentType }}</span>
                  </div>
                  <h6 class="hr-info-card-title">{{ doc.name || 'Untitled Document' }}</h6>
                  <div class="hr-info-card-body hr-fs-sm">
                    <p>Number: {{ doc.documentNumber || 'N/A' }}</p>
                    <p>Uploaded: {{ formatDate(doc.uploadDate) }}</p>
                    <p v-if="doc.validTill">Valid until: {{ formatDate(doc.validTill) }}</p>
                    <p v-if="doc.fileSize">Size: {{ formatFileSize(doc.fileSize) }}</p>
                  </div>
                  <div class="hr-info-card-footer hr-flex hr-align-center hr-justify-between">
                    <span v-if="doc.isVerified === '1'" class="hr-badge hr-badge-success"><i class="bx bx-check-circle"></i> Verified</span>
                    <span v-else class="hr-badge hr-badge-warning"><i class="bx bx-time"></i> Pending</span>
                    <div class="hr-flex hr-gap-1">
                      <button v-if="doc.filePath" type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="viewDocument(doc)" title="View"><i class="bx bx-show"></i></button>
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-secondary" title="Download"><i class="bx bx-download"></i></button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Emergency Contacts -->
            <div v-show="activeTab === 'contacts'" class="tab-pane">
              <div v-if="emergencyContacts.length === 0" class="hr-empty-state">
                <i class="bx bx-phone-call hr-empty-icon"></i>
                <h5>No emergency contacts</h5>
                <p class="hr-text-muted">No emergency contacts have been added for this employee</p>
              </div>
              <div v-else class="hr-cards-grid">
                <div v-for="(contact, index) in emergencyContacts" :key="index" class="hr-info-card">
                  <div class="hr-info-card-header">
                    <div class="hr-info-card-icon hr-info-card-icon-warning"><i class="bx bx-user-circle"></i></div>
                    <span class="hr-badge hr-badge-warning">Emergency Contact</span>
                  </div>
                  <h6 class="hr-info-card-title">{{ contact.name || 'N/A' }}</h6>
                  <div class="hr-info-card-body">
                    <p><i class="bx bx-phone"></i> <a :href="'tel:' + contact.phoneNumber">{{ contact.phoneNumber || 'N/A' }}</a></p>
                    <p v-if="contact.email"><i class="bx bx-envelope"></i> <a :href="'mailto:' + contact.email">{{ contact.email }}</a></p>
                    <p><i class="bx bx-heart"></i> Relationship: {{ contact.relationship || 'N/A' }}</p>
                  </div>
                  <div class="hr-info-card-footer hr-flex hr-gap-2">
                    <button type="button" class="hr-btn hr-btn-sm hr-btn-success" @click="callContact(contact.phoneNumber)"><i class="bx bx-phone"></i> Call</button>
                    <button v-if="contact.email" type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="emailContact(contact.email)"><i class="bx bx-envelope"></i> Email</button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Next of Kin -->
            <div v-show="activeTab === 'kin'" class="tab-pane">
              <div v-if="nextOfKin.length === 0" class="hr-empty-state">
                <i class="bx bx-heart hr-empty-icon"></i>
                <h5>No next of kin records</h5>
                <p class="hr-text-muted">No next of kin information has been added for this employee</p>
              </div>
              <div v-else class="hr-cards-grid">
                <div v-for="(kin, index) in nextOfKin" :key="index" class="hr-info-card">
                  <div class="hr-info-card-header">
                    <div class="hr-info-card-icon hr-info-card-icon-info"><i class="bx bx-heart"></i></div>
                    <span class="hr-badge hr-badge-info">Next of Kin</span>
                  </div>
                  <h6 class="hr-info-card-title">{{ kin.name || 'N/A' }}</h6>
                  <div class="hr-info-card-body">
                    <p><i class="bx bx-phone"></i> <a :href="'tel:' + kin.phoneNumber">{{ kin.phoneNumber || 'N/A' }}</a></p>
                    <p v-if="kin.email"><i class="bx bx-envelope"></i> <a :href="'mailto:' + kin.email">{{ kin.email }}</a></p>
                    <p><i class="bx bx-heart"></i> Relationship: {{ kin.relationship || 'N/A' }}</p>
                    <p>
                      <span v-if="kin.isCoveredInBenefits === '1'" class="hr-badge hr-badge-success"><i class="bx bx-check-circle"></i> Covered in Benefits</span>
                      <span v-else class="hr-badge hr-badge-muted"><i class="bx bx-x-circle"></i> Not Covered</span>
                    </p>
                  </div>
                  <div class="hr-info-card-footer hr-flex hr-gap-2">
                    <button type="button" class="hr-btn hr-btn-sm hr-btn-success" @click="callContact(kin.phoneNumber)"><i class="bx bx-phone"></i> Call</button>
                    <button v-if="kin.email" type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="emailContact(kin.email)"><i class="bx bx-envelope"></i> Email</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </template>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, uploads } from "@/api";
import { maskEmail, maskPhone, maskPlain, maskNationalId, maskDateOfBirth } from "@/utils/sensitiveEmployeeFields";

export default {
  name: "EmployeeProfile",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      sensitiveRevealed: false,
      activeTab: "personal",
      employee: null,
      governmentIds: [],
      documents: [],
      emergencyContacts: [],
      nextOfKin: [],
      contactForm: { email: "", phone: "" },
      savingContact: false,
      contactSaveMsg: "",
      contactSaveOk: false
    };
  },

  computed: {
    contactDirty() {
      if (!this.employee) return false;
      const e = (this.contactForm.email || "").trim();
      const p = (this.contactForm.phone || "").trim();
      return e !== (this.employee.email || "").trim() || p !== (this.employee.phone || "").trim();
    }
  },

  mounted() {
    this.loadEmployeeDetails();
  },

  methods: {
    syncContactForm() {
      if (!this.employee) return;
      this.contactForm = {
        email: (this.employee.email || "").trim(),
        phone: (this.employee.phone || "").trim()
      };
      this.contactSaveMsg = "";
    },

    async saveContactDetails() {
      const email = (this.contactForm.email || "").trim();
      const phone = (this.contactForm.phone || "").trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.contactSaveOk = false;
        this.contactSaveMsg = "Enter a valid email address.";
        return;
      }
      const digits = phone.replace(/\D/g, "");
      if (!phone || digits.length < 9 || digits.length > 15) {
        this.contactSaveOk = false;
        this.contactSaveMsg = "Enter a valid phone number.";
        return;
      }
      this.savingContact = true;
      this.contactSaveMsg = "";
      try {
        const fd = new FormData();
        fd.append("function", "updateEmployeeSelfContact");
        fd.append("email", email);
        fd.append("phone", phone);
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          const d = res.data.data || {};
          if (this.employee) {
            this.employee.email = d.email != null ? d.email : email;
            this.employee.phone = d.phone != null ? d.phone : phone;
          }
          this.syncContactForm();
          this.contactSaveOk = true;
          this.contactSaveMsg = res.data.message || "Saved.";
          this.$toast?.success?.(this.contactSaveMsg) || null;
        } else {
          this.contactSaveOk = false;
          this.contactSaveMsg = res?.data?.message || "Could not save.";
          this.$toast?.error?.(this.contactSaveMsg) || null;
        }
      } catch (err) {
        this.contactSaveOk = false;
        this.contactSaveMsg = err?.response?.data?.message || "Could not save.";
        this.$toast?.error?.(this.contactSaveMsg) || null;
      } finally {
        this.savingContact = false;
      }
    },

    async loadEmployeeDetails() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getEmployeeProfile");
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          const empData = res.data.data;
          this.employee = empData.employee;
          this.governmentIds = empData.governmentIds || [];
          this.documents = empData.documents || [];
          this.emergencyContacts = empData.emergencyContacts || [];
          this.nextOfKin = empData.nextOfKin || [];
          this.syncContactForm();
        } else {
          throw new Error(res.data?.message || "Failed to load employee details");
        }
      } catch (error) {
        this.$toast?.error?.("Failed to load employee details: " + error.message) || console.error("Failed to load employee details:", error);
      } finally {
        this.loading = false;
      }
    },

    displayEmail(val) {
      if (this.sensitiveRevealed) return val || "—";
      return maskEmail(val);
    },
    displayPhone(val) {
      if (this.sensitiveRevealed) return val || "—";
      return maskPhone(val);
    },
    displayNationalId(val) {
      if (this.sensitiveRevealed) return val || "—";
      return maskNationalId(val);
    },
    displayDateOfBirth(val) {
      if (this.sensitiveRevealed) return this.formatDate(val);
      return maskDateOfBirth(val);
    },
    displayPayGroup(val) {
      if (this.sensitiveRevealed) return val || "—";
      return maskPlain(val);
    },
    displayEmploymentType(val) {
      if (this.sensitiveRevealed) return val || "N/A";
      const s = String(val || "").trim();
      return s ? maskPlain(s) : "N/A";
    },

    getInitials(firstName, lastName) {
      return ((firstName ? firstName.charAt(0) : "") + (lastName ? lastName.charAt(0) : "")).toUpperCase();
    },

    getFullName() {
      if (!this.employee) return "N/A";
      const parts = [this.employee.firstName, this.employee.middleName, this.employee.lastName].filter(Boolean);
      return parts.join(" ") || "N/A";
    },

    getStatusClass(status) {
      const s = status?.toLowerCase();
      if (s === "active") return "hr-badge-success";
      if (s === "resigned" || s === "terminated") return "hr-badge-danger";
      if (s === "suspended") return "hr-badge-warning";
      return "hr-badge-secondary";
    },

    getEmploymentTypeClass(type) {
      const map = { Permanent: "hr-badge-success", Contract: "hr-badge-info", Temporary: "hr-badge-warning", Intern: "hr-badge-primary", Consultant: "hr-badge-secondary" };
      return map[type] || "hr-badge-secondary";
    },

    formatIdType(idType) {
      const names = { NATIONAL_ID: "National ID", KRA_PIN: "KRA PIN", NHIF: "NHIF Number", NSSF: "NSSF Number", SHIF: "SHIF Number", PASSPORT: "Passport", DRIVING_LICENSE: "Driving License", BIRTH_CERT: "Birth Certificate" };
      return names[idType] || idType?.replace(/_/g, " ") || "Unknown";
    },

    getIdTypeClass(idType) {
      const map = { NATIONAL_ID: "hr-badge-primary", KRA_PIN: "hr-badge-success", NHIF: "hr-badge-info", NSSF: "hr-badge-warning", SHIF: "hr-badge-secondary", PASSPORT: "hr-badge-danger", DRIVING_LICENSE: "hr-badge-secondary", BIRTH_CERT: "hr-badge-muted" };
      return map[idType] || "hr-badge-secondary";
    },

    isExpired(date) {
      if (!date) return false;
      return new Date(date) < new Date();
    },

    isExpiringSoon(date) {
      if (!date) return false;
      const expiry = new Date(date);
      const now = new Date();
      const in30 = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      return expiry > now && expiry <= in30;
    },

    formatDate(date) {
      if (!date) return "N/A";
      return new Date(date).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    },

    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return "N/A";
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i];
    },

    viewDocument(doc) {
      if (doc.filePath) {
        window.open(uploads + "" + doc.filePath, "_blank");
      } else {
        this.$toast?.info?.("Document file not available") || alert("Document file not available");
      }
    },

    callContact(phoneNumber) {
      if (phoneNumber) window.location.href = `tel:${phoneNumber}`;
    },

    emailContact(email) {
      if (email) window.location.href = `mailto:${email}`;
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.employee-profile-content { padding: 0; }

.profile-summary-grid {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 24px;
  align-items: center;
}
@media (max-width: 768px) {
  .profile-summary-grid { grid-template-columns: 1fr; text-align: center; }
}

.profile-avatar-wrap { flex-shrink: 0; }
.profile-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #059669 0%, #16a34a 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
}
.profile-summary-main { min-width: 0; }
.profile-meta { font-size: 14px; }
.profile-meta i { margin-right: 6px; }
.hr-mb-1 { margin-bottom: 4px; }
.hr-ml-1 { margin-left: 6px; }
.hr-mt-1 { margin-top: 4px; }
.hr-fs-sm { font-size: 12px; }

.hr-tabs-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 0 24px;
  border-bottom: 2px solid #e2e8f0;
  background: #f8fafc;
}
.hr-tab {
  padding: 14px 18px;
  border: none;
  background: none;
  color: #64748b;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: color 0.2s, border-color 0.2s;
}
.hr-tab:hover { color: #059669; }
.hr-tab-active { color: #059669; border-bottom-color: #059669; }
.hr-badge-sm { font-size: 11px; padding: 2px 8px; }

.tab-content-wrap { padding-top: 20px; }
.tab-pane { min-height: 120px; }

.profile-detail-grid { grid-template-columns: 1fr 1fr; gap: 32px; }
@media (max-width: 768px) { .profile-detail-grid { grid-template-columns: 1fr; } }

.hr-table-borderless td { border: none; padding: 8px 0; vertical-align: top; }
.hr-label-cell { width: 40%; font-weight: 600; color: #64748b; }

.hr-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}
.hr-info-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  background: #fff;
  transition: box-shadow 0.2s;
}
.hr-info-card:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
.hr-info-card-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px; }
.hr-info-card-icon {
  width: 40px; height: 40px;
  display: flex; align-items: center; justify-content: center;
  background: #dbeafe; color: #2563eb; border-radius: 10px;
  font-size: 20px;
}
.hr-info-card-icon-warning { background: #fef3c7; color: #b45309; }
.hr-info-card-icon-info { background: #e0f2fe; color: #0369a1; }
.hr-info-card-title { margin: 0 0 12px; font-size: 16px; font-weight: 600; }
.hr-info-card-body p { margin: 0 0 8px; color: #64748b; font-size: 13px; }
.hr-info-card-body i { margin-right: 6px; }
.hr-info-card-footer { margin-top: 16px; padding-top: 12px; border-top: 1px solid #e2e8f0; }

.hr-flex { display: flex; }
.hr-gap-1 { gap: 8px; }
.hr-gap-2 { gap: 12px; }
.hr-gap-3 { gap: 16px; }
.hr-flex-wrap { flex-wrap: wrap; }
.hr-align-center { align-items: center; }
.hr-justify-between { justify-content: space-between; }
.sensitive-toggle { flex-shrink: 0; white-space: nowrap; }
.hr-mb-2 { margin-bottom: 8px; }
.hr-mb-4 { margin-bottom: 16px; }
.hr-mt-4 { margin-top: 16px; }

.profile-contact-edit {
  padding: 18px 20px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 100%);
  border-left: 4px solid #059669;
}
.profile-contact-edit__head {
  margin-bottom: 14px;
}
.profile-contact-edit__title {
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 8px;
}
.profile-contact-fields {
  display: grid;
  grid-template-columns: minmax(100px, 140px) minmax(0, 1fr);
  gap: 12px 16px;
  align-items: center;
  width: 100%;
  max-width: 640px;
}
.profile-contact-label {
  font-weight: 600;
  font-size: 13px;
  color: #475569;
  margin: 0;
}
.profile-contact-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 14px;
  color: #0f172a;
  background: #fff;
  box-sizing: border-box;
}
.profile-contact-input:focus {
  outline: none;
  border-color: #059669;
  box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.15);
}
.profile-contact-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
}
.profile-contact-msg {
  font-size: 13px;
}
.text-success { color: #059669 !important; }
.text-danger { color: #dc2626 !important; }
</style>
